import { Injectable, signal } from '@angular/core';
import { StorageKey } from '../../constants/storage.constant';

export interface IAuthUserData {
  name?: string;
  email: string;
  /**
   * Carries the registration Privacy Policy acknowledgement across the OTP step,
   * since the checkbox is collected on signup-account-info but POST /auth/register
   * is only called later from signup-password. Optional so the reset-password flow,
   * which never presents the checkbox, is unaffected.
   *
   * Not authoritative: the server independently rejects a registration without it
   * (spec FR-020), so a tampered stored value cannot bypass the requirement.
   */
  approve_privacy_policy?: boolean;
}

/**
 * Centralized authentication storage service
 * Manages all authentication-related localStorage operations using signals
 */
@Injectable({
  providedIn: 'root',
})
export class StorageAuthService {
  // User ID Signal
  private readonly otpIdSignal = signal<string>(
    this.getFromStorage(StorageKey.OTP_ID)
  );
  readonly otpId = this.otpIdSignal.asReadonly();

  // Signup User Signal
  private readonly AuthUserDataSignal = signal<IAuthUserData | null>(
    this.getStoredAuthUserData()
  );
  readonly AuthUserData = this.AuthUserDataSignal.asReadonly();

  // OTP Token Signal
  private readonly otpTokenSignal = signal<string>(
    this.getFromStorage(StorageKey.OTP_TOKEN)
  );
  readonly otpToken = this.otpTokenSignal.asReadonly();

  /**
   * Get value from localStorage
   */
  private getFromStorage(key: string): string {
    return localStorage.getItem(key) ?? '';
  }

  /**
   * Get stored signup user from localStorage
   */
  private getStoredAuthUserData(): IAuthUserData | null {
    const stored = localStorage.getItem(StorageKey.AUTH_USER);
    return stored ? JSON.parse(stored) : null;
  }

  // ========== USER ID OPERATIONS ==========

  /**
   * Get user ID value
   */
  getOtpId(): string {
    return this.otpIdSignal();
  }

  /**
   * Update user ID in signal and localStorage
   */
  setOtpId(otpId: string): void {
    this.otpIdSignal.set(otpId);
    localStorage.setItem(StorageKey.OTP_ID, otpId);
  }

  /**
   * Clear user ID
   */
  clearOtpId(): void {
    this.otpIdSignal.set('');
    localStorage.removeItem(StorageKey.OTP_ID);
  }

  // ========== SIGNUP USER OPERATIONS ==========

  /**
   * Get signup user data
   */
  getAuthUserData(): IAuthUserData | null {
    return this.AuthUserDataSignal();
  }

  /**
   * Update signup user data in signal and localStorage
   */
  setAuthUserData(user: IAuthUserData): void {
    this.AuthUserDataSignal.set(user);
    localStorage.setItem(StorageKey.AUTH_USER, JSON.stringify(user));
  }

  /**
   * Clear signup user data
   */
  clearAuthUserData(): void {
    this.AuthUserDataSignal.set(null);
    localStorage.removeItem(StorageKey.AUTH_USER);
  }

  // ========== OTP TOKEN OPERATIONS ==========

  /**
   * Get OTP token value
   */
  getOtpToken(): string {
    return this.otpTokenSignal();
  }

  /**
   * Update OTP token in signal and localStorage
   */
  setOtpToken(token: string): void {
    this.otpTokenSignal.set(token);
    localStorage.setItem(StorageKey.OTP_TOKEN, token);
  }

  /**
   * Clear OTP token
   */
  clearOtpToken(): void {
    this.otpTokenSignal.set('');
    localStorage.removeItem(StorageKey.OTP_TOKEN);
  }

  // ========== BULK OPERATIONS ==========

  /**
   * Clear all authentication-related data
   */
  clearAll(): void {
    this.clearOtpId();
    this.clearAuthUserData();
    this.clearOtpToken();
  }
}
